import { createSelector, createSlice } from '@reduxjs/toolkit';
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, initMapFactory } from '../common/reducers/utilities';
import { ensureArray } from '../common/utils/arrayUtilities';

const compareMembers = (m1, m2) => m1.id === m2.id;

const crewMemberSlice = createSlice({
    name: 'crewMember',
    initialState: {
        data: {}
    },
    reducers: {
        set: (state, action) => {
            action.payload.forEach(r => delete state.data[r.callsign]);
            action.payload.forEach(r => {
                if (!state.data[r.callsign]) {
                    state.data[r.callsign] = [];
                }
                let idx = state.data[r.callsign].findIndex(r2 => compareMembers(r, r2));
                if (idx >= 0) {
                    state.data[r.callsign].splice(idx, 1, r);
                } else {
                    state.data[r.callsign].push(r);
                }
            })
        },
        addOrUpdate: (state, action) => {
            for (const member of ensureArray(action.payload)) {
                if (!state.data[member.callsign]) {
                    state.data[member.callsign] = [];
                }
                let idx = state.data[member.callsign].findIndex(r2 => compareMembers(member, r2));
                if (idx >= 0) {
                    state.data[member.callsign].splice(idx, 1, member);
                } else {
                    state.data[member.callsign].push(member);
                }
            }
        },
        remove: (state, action) => {
            for (const member of ensureArray(action.payload)) {
                let idx = state.data[member.callsign].findIndex(r => compareMembers(r, member));
                if (idx >= 0) {
                    state.data[member.callsign].splice(idx, 1);
                }
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => crewMemberSlice.getInitialState());
    }
});

// #region network operations
const fetchMembers = callsign => network => network.get('/api/v1/crewmembers/' + encodeURIComponent(callsign), crewMemberSlice.actions.set);
export const insertCrewMember = (network, member) => network.post('/api/v1/crewmember', member, crewMemberSlice.actions.addOrUpdate);
export const updateCrewMember = (network, member) => network.put('/api/v1/crewmember', member, crewMemberSlice.actions.addOrUpdate);
export const removeCrewMember = (network, member) => network.delete('/api/v1/crewmember/' + encodeURIComponent(member.id), crewMemberSlice.actions.remove);
// #endregion

// #region selectors
const selectAllCrewMembers = state => state.crewMember?.data;
const selectMembersForCrew = createSelector([selectAllCrewMembers, (state, callsign) => callsign], (members, callsign) => members?.[callsign] ?? []);
const selectCrewMembers = callsign => state => selectMembersForCrew(state, callsign);
// #endregion

// #region listeners
const listen = {
    crewMemberAdded: (member, { dispatch }) => dispatch(crewMemberSlice.actions.addOrUpdate(member)),
    crewMemberUpdated: (member, { dispatch }) => dispatch(crewMemberSlice.actions.addOrUpdate(member)),
    crewMemberRemoved: (member, { dispatch }) => dispatch(crewMemberSlice.actions.remove(member))
}
// #endregion

export const [useCrewMembers] = dataHookFactory(selectCrewMembers, initMapFactory(fetchMembers, listen));

const reducerDetails = { name: crewMemberSlice.name, reducer: crewMemberSlice.reducer };
export default reducerDetails;